import { useState, useEffect } from 'react';

const technologies = [
  {
    id: 'uf',
    type: 'light-blue' as const,
    icon: 'ri-filter-3-line',
    name: 'Ultrafiltration',
    short: 'UF',
    description: 'Separation of high- and low-molecular components for effective wastewater treatment.',
    benefits: ['Environmental safety', 'Reduced water consumption costs', 'High purification efficiency', 'Long membrane lifespan'],
    features: ['Hollow fiber membranes', 'Pore size 0.01–0.1 µm', 'Gravity or pressure-driven', 'Modular scalable design'],
    applications: ['Circulating systems', 'Pretreatment', 'Wastewater treatment', 'Drinking water preparation'],
  },
  {
    id: 'zld',
    type: 'gradient' as const,
    icon: 'ri-recycle-line',
    name: 'ZLD Systems',
    short: 'ZLD',
    description: 'Zero Liquid Discharge — eliminate liquid discharge completely with a closed-loop system.',
    components: ['Ultrafiltration', 'Reverse Osmosis', 'Evaporation', 'Dewatering'],
    advantages: ['Closed loop system', 'Environmental benefit', 'Cost savings', 'Regulatory compliance'],
  },
  {
    id: 'ro',
    type: 'photo' as const,
    icon: 'ri-water-flash-line',
    name: 'Reverse Osmosis',
    short: 'RO',
    photo: 'https://public.readdy.ai/ai/img_res/bf23ed8c2b4a38de94936fe23896c243.jpg',
    description: 'Multi-stage purification with maximum permeate yield and reduced disposal costs.',
    stats: [{ label: 'Permeate yield', value: '95%+' }, { label: 'Concentrate', value: '<5%' }],
    stages: ['Preliminary separation', 'Highest purification', 'Concentrate reduction', 'Disc osmosis'],
    advantages: ['Removes up to 99% of dissolved salts', 'Minimal chemical consumption', 'Compact modular design', 'Low operating costs'],
  },
  {
    id: 'dtro',
    type: 'dark' as const,
    icon: 'ri-disc-line',
    name: 'Disc Osmosis (DTRO)',
    short: 'DTRO',
    description: 'Advanced membrane technology with superior performance characteristics.',
    highlights: [
      { value: '10×', label: 'Reduced cleaning frequency' },
      { value: '+30%', label: 'Permeate yield increase' },
      { value: '5+ yrs', label: 'Membrane lifespan' },
    ],
    applications: ['Industrial wastewater', 'Chemical industry', 'Oil & gas', 'ZLD systems'],
    advantages: ['Handles highly concentrated feeds', 'Open-channel disc design prevents fouling', 'Extended membrane service life', 'Ideal for ZLD & high-TDS streams'],
  },
  {
    id: 'stro',
    type: 'stro' as const,
    icon: 'ri-node-tree',
    name: 'Spacer Tube RO (STRO)',
    short: 'STRO',
    description: 'Specialized reverse osmosis technology for highly mineralized wastewater, landfill leachate and industrial effluents. Open-channel design prevents fouling and ensures stable long-term operation.',
    highlights: [
      { value: 'High', label: 'Salt & hardness removal' },
      { value: 'Open', label: 'Anti-fouling channel design' },
      { value: 'Stable', label: 'Long-term performance' },
    ],
    applications: ['Highly mineralized wastewater', 'Landfill leachate', 'Industrial effluents', 'ZLD systems'],
    advantages: ['Removes hardness salts & organics', 'Suspended solids removal', 'Anti-clogging open channel', 'Compatible with DTRO systems'],
  },
  {
    id: 'edi',
    type: 'icon' as const,
    icon: 'ri-flashlight-line',
    name: 'Electrodeionization',
    short: 'EDI',
    description: 'Supply of IonPure module analogs with full compatibility and competitive pricing.',
    benefits: ['Full compatibility', 'Similar characteristics', 'Competitive price', 'Fast delivery'],
    features: ['High purity water', 'Continuous operation', 'No chemical regeneration', 'Low maintenance'],
  },
];

export default function Technologies() {
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);
  const tech = technologies[active];

  useEffect(() => {
    const handler = (e: Event) => {
      const index = (e as CustomEvent<{ index: number }>).detail.index;
      if (index >= 0 && index < technologies.length) {
        setActive(index);
      }
    };
    window.addEventListener('selectTechnology', handler);
    return () => window.removeEventListener('selectTechnology', handler);
  }, []);

  return (
    <section id="technologies" className="bg-gradient-to-br from-white via-[#F7FBFF] to-[#EAF2FF] py-16 lg:py-24 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-[2px] bg-[#0A1628]"></div>
          <span className="text-xs uppercase tracking-[3px] text-[#0A1628]/50 font-bold">Technologies</span>
        </div>
        <h2 className="text-[32px] sm:text-[40px] lg:text-[52px] font-black text-[#0A1628] uppercase leading-none mb-10 lg:mb-16 tracking-tight">
          ADVANCED PURIFICATION<br />TECHNOLOGIES
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">

          {/* Left: Tab list */}
          <div className="flex flex-col gap-1">
            {/* Hint label */}
            <div className="hidden lg:flex items-center gap-2 mb-3 px-1">
              <i className="ri-cursor-line text-[#0066FF] text-sm"></i>
              <span className="text-[11px] text-[#0A1628]/40 font-semibold uppercase tracking-widest">Select technology</span>
            </div>

            <div className="flex flex-row lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0 lg:overflow-x-visible">
              {technologies.map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  className={`rounded-[16px] p-4 lg:p-5 text-left transition-all duration-200 cursor-pointer flex items-center gap-3 lg:gap-4 flex-shrink-0 lg:flex-shrink relative overflow-hidden ${
                    active === i
                      ? 'bg-[#0A1628] shadow-lg scale-[1.02]'
                      : hovered === i
                      ? 'bg-[#EBF4FF] border border-[#0066FF]/30 shadow-md scale-[1.01]'
                      : 'bg-[#F7FAFF] border border-[#E8F0FF] hover:border-[#0A1628]/30'
                  }`}
                >
                  {/* Hover accent line */}
                  {active !== i && (
                    <div className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[16px] transition-all duration-200 ${
                      hovered === i ? 'bg-[#0066FF] opacity-100' : 'opacity-0'
                    }`}></div>
                  )}
                  {/* Active accent line */}
                  {active === i && (
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[16px] bg-[#0066FF]"></div>
                  )}

                  <div className={`w-9 h-9 lg:w-10 lg:h-10 flex items-center justify-center rounded-[10px] flex-shrink-0 transition-all duration-200 ${
                    active === i
                      ? 'bg-white/10'
                      : hovered === i
                      ? 'bg-[#0066FF]/10 border border-[#0066FF]/20'
                      : 'bg-white border border-[#E8F0FF]'
                  }`}>
                    <i className={`${t.icon} text-[16px] lg:text-[18px] transition-colors duration-200 ${
                      active === i ? 'text-white' : hovered === i ? 'text-[#0066FF]' : 'text-[#0A1628]'
                    }`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[10px] font-black uppercase tracking-widest mb-0.5 transition-colors duration-200 ${
                      active === i ? 'text-white/40' : hovered === i ? 'text-[#0066FF]/60' : 'text-[#0A1628]/40'
                    }`}>{t.short}</div>
                    <div className={`text-sm font-bold whitespace-nowrap transition-colors duration-200 ${
                      active === i ? 'text-white' : hovered === i ? 'text-[#0A1628]' : 'text-[#0A1628]'
                    }`}>{t.name}</div>
                  </div>
                  <div className="ml-auto w-5 h-5 items-center justify-center hidden lg:flex flex-shrink-0">
                    {active === i
                      ? <i className="ri-arrow-right-s-line text-white/60 text-lg"></i>
                      : <i className={`ri-arrow-right-s-line text-lg transition-all duration-200 ${hovered === i ? 'text-[#0066FF] translate-x-0.5' : 'text-[#0A1628]/20'}`}></i>
                    }
                  </div>
                </button>
              ))}
            </div>

            {/* Mobile scroll hint */}
            <div className="flex lg:hidden items-center justify-center gap-1.5 mt-2">
              {technologies.map((_, i) => (
                <div key={i} className={`rounded-full transition-all duration-200 ${
                  active === i ? 'w-4 h-1.5 bg-[#0A1628]' : 'w-1.5 h-1.5 bg-[#0A1628]/20'
                }`}></div>
              ))}
            </div>
          </div>

          {/* Right: Detail cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Main card */}
            <div
              key={`main-${tech.id}`}
              className={`rounded-[24px] p-7 lg:p-10 flex flex-col justify-between sm:col-span-1 sm:row-span-2 ${
                tech.type === 'light-blue' ? 'bg-[#EBF4FF]' :
                tech.type === 'gradient' ? 'bg-gradient-to-br from-[#0A1628] to-[#1a3a6e]' :
                tech.type === 'dark' ? 'bg-[#0A1628]' :
                tech.type === 'icon' ? 'bg-white border border-[#E8F0FF]' :
                tech.type === 'stro' ? 'bg-gradient-to-br from-[#0A2A1A] to-[#0D3D28]' :
                'bg-[#0A1628]'
              } ${tech.type === 'photo' ? 'relative overflow-hidden min-h-[400px]' : ''}`}
            >

              {/* Photo background for RO */}
              {tech.type === 'photo' && tech.photo && (
                <img
                  src={tech.photo}
                  alt={tech.name}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              )}
              {tech.type === 'photo' && (
                <div className="absolute inset-0 bg-[#0A1628]/65"></div>
              )}

              <div className={`${tech.type === 'photo' ? 'relative z-10 flex flex-col justify-between h-full' : ''}`}>
                <div>
                  <div className={`text-[56px] lg:text-[88px] font-black leading-none opacity-[0.07] mb-2 tracking-tighter ${
                    tech.type === 'light-blue' || tech.type === 'icon' ? 'text-[#0A1628]' : 'text-white'
                  }`}>{tech.short}</div>
                  <div className="w-10 h-10 flex items-center justify-center mb-5">
                    <i className={`${tech.icon} text-[28px] ${
                      tech.type === 'light-blue' || tech.type === 'icon' ? 'text-[#0A1628]' :
                      tech.type === 'stro' ? 'text-[#4ADE80]' : 'text-white'
                    }`}></i>
                  </div>
                  <h3 className={`text-xl font-black uppercase tracking-tight mb-3 ${
                    tech.type === 'light-blue' || tech.type === 'icon' ? 'text-[#0A1628]' : 'text-white'
                  }`}>{tech.name}</h3>
                  <p className={`text-sm leading-relaxed mb-6 ${
                    tech.type === 'light-blue' || tech.type === 'icon' ? 'text-[#4A5568]' :
                    tech.type === 'gradient' ? 'text-white/70' :
                    tech.type === 'photo' ? 'text-white/75' :
                    tech.type === 'stro' ? 'text-white/70' :
                    'text-white/60'
                  }`}>{tech.description}</p>
                </div>

                {/* ZLD components */}
                <div style={{ display: tech.type === 'gradient' && tech.components ? 'block' : 'none' }}>
                  {tech.type === 'gradient' && tech.components && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[3px] text-white/40 font-bold mb-3">Components</div>
                      <div className="flex flex-wrap gap-2">
                        {tech.components.map((c, i) => (
                          <span key={i} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-semibold">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* UF / EDI benefits */}
                <div style={{ display: (tech.type === 'light-blue' || tech.type === 'icon') && tech.benefits ? 'block' : 'none' }}>
                  {(tech.type === 'light-blue' || tech.type === 'icon') && tech.benefits && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[3px] text-[#0A1628]/40 font-bold mb-3">Benefits</div>
                      <ul className="space-y-2">
                        {tech.benefits.map((b, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-[#0A1628]">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#0A1628]/30 flex-shrink-0"></div>
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* RO stats */}
                <div style={{ display: tech.type === 'photo' && tech.stats ? 'block' : 'none' }}>
                  {tech.type === 'photo' && tech.stats && (
                    <div className="grid grid-cols-2 gap-3 mt-6">
                      {tech.stats.map((s, i) => (
                        <div key={i} className="rounded-[12px] bg-white/10 border border-white/15 p-4">
                          <div className="text-3xl font-black text-white leading-none mb-1">{s.value}</div>
                          <div className="text-xs text-white/50">{s.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* DTRO highlights */}
                <div style={{ display: tech.type === 'dark' && tech.highlights ? 'block' : 'none' }}>
                  {tech.type === 'dark' && tech.highlights && (
                    <div className="space-y-3">
                      {tech.highlights.map((h, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 rounded-[12px] bg-white/5 border border-white/10">
                          <div className="text-3xl font-black text-white leading-none w-20 flex-shrink-0">{h.value}</div>
                          <div className="text-sm text-white/60">{h.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* STRO highlights */}
                <div style={{ display: tech.type === 'stro' && tech.highlights ? 'block' : 'none' }}>
                  {tech.type === 'stro' && tech.highlights && (
                    <div className="space-y-3">
                      {tech.highlights.map((h, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 rounded-[12px] bg-white/5 border border-[#4ADE80]/20">
                          <div className="text-2xl font-black text-[#4ADE80] leading-none w-20 flex-shrink-0">{h.value}</div>
                          <div className="text-sm text-white/60">{h.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Applications card */}
            <div key={`apps-${tech.id}`} className="rounded-[24px] bg-[#F7FAFF] border border-[#E8F0FF] p-7 flex flex-col">
              <div className="text-[10px] uppercase tracking-[3px] text-[#0A1628]/40 font-bold mb-4">Applications</div>
              <div className="flex flex-wrap gap-2 flex-1 content-start">
                {(tech.applications || tech.components || tech.stages || tech.features)?.map((item, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full bg-white border border-[#E8F0FF] text-[#0A1628] text-xs font-semibold">{item}</span>
                ))}
              </div>
            </div>

            {/* Key Advantages card */}
            <div key={`adv-${tech.id}`} className="rounded-[24px] bg-[#0A1628] p-7 flex flex-col">
              <div className="text-[10px] uppercase tracking-[3px] text-white/40 font-bold mb-4">Key Advantages</div>
              <ul className="space-y-2 flex-1">
                {(tech.advantages || (tech.type === 'icon' || tech.type === 'light-blue' ? tech.features : tech.benefits) || tech.features)?.slice(0, 4).map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-white/80">
                    <i className={`ri-arrow-right-s-line text-base flex-shrink-0 ${tech.type === 'stro' ? 'text-[#4ADE80]/50' : 'text-white/30'}`}></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
